import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorPage from '../pages/ErrorPage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualiza el estado para que el siguiente render muestre la UI de error
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Puedes registrar el error en un servicio de logging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Llamar al callback de error si existe
    this.props.onError?.(error, errorInfo);
    
    // Actualizar el estado con la información del error
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Puedes renderizar cualquier UI de error personalizada
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de error por defecto
      return (
        <ErrorPage
          errorCode="500"
          title="Algo salió mal"
          message="Hemos encontrado un error inesperado. Nuestro equipo ha sido notificado y estamos trabajando para solucionarlo."
          showBackButton={true}
          showHomeButton={true}
        />
      );
    }

    return this.props.children;
  }
}

// Hook personalizado para manejar errores en componentes funcionales
export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  const handleError = React.useCallback((error: Error) => {
    console.error('Error caught by useErrorHandler:', error);
    setError(error);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return { error, handleError, clearError };
}

// Componente de error genérico para diferentes tipos de errores
export function GenericError({ 
  error
}: { 
  error: Error; 
}) {
  return (
    <ErrorPage
      errorCode="500"
      title="Error inesperado"
      message={error.message || "Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo."}
      showBackButton={true}
      showHomeButton={true}
    />
  );
}
